// INPUT SIZING (density pass, Nov 3 2026 — owner: "efficiency; scrolling up
// and down to see information is not ideal"). Inputs were py-2.5 (~42px) on
// every screen; they are now ~34px from `sm:` up. Below `sm:` they stay ~42px
// AND 16px text: iOS Safari zooms the whole page on focusing any field under
// 16px, which made every phone form jump. So the size is RESPONSIVE, which
// has a consequence: an unprefixed override appended to inputCls (`text-xs`,
// `py-1`) LOSES to the `sm:` classes on desktop. For a smaller field use
// inputSmCls / selectSmCls / textareaSmCls, never `${inputCls} text-xs py-1`.
export const inputBaseCls =
  "w-full bg-surface border border-zinc-300 rounded-md px-2.5 text-ink " +
  "placeholder-ink-subtle focus:outline-none focus:border-brand-600 focus:ring-1 " +
  "focus:ring-brand-600 transition-colors";

export const inputCls = `${inputBaseCls} py-2 sm:py-1.5 text-base sm:text-sm`;

// Compact field (~28px on desktop): inline row editors, toolbar selects, notes.
export const inputSmCls = `${inputBaseCls} py-1.5 sm:py-1 text-base sm:text-xs`;

export const textareaCls = `${inputCls} resize-y`;
export const textareaSmCls = `${inputSmCls} resize-y`;

// A field sized to its content instead of stretching to the column. `w` is a
// Tailwind width (use FIELD_W). ⚠️ Strips w-full first: appending a width to a
// w-full class does NOT reliably win (see the selectCls note in CLAUDE.md).
export const sized = (cls, w) => `${cls.replace("w-full", "").trim()} ${w}`;

// Content-based widths for numeric/short fields, and right-aligned figures.
export const FIELD_W = {
  xs: "w-20",   // qty, hours, %, 1–10 weight
  sm: "w-28",   // money, rates
  md: "w-36",   // dates, times
  lg: "w-48",   // short text / short selects
};
export const numCls = "text-right tabular-nums";

// ⚠️ `select-flat` (index.css) resets the browser's native <select> chrome —
// without it every dropdown in the app rendered the OS's own widget (a
// visibly puffier grey control with its own arrow on macOS, flatter on
// Windows/Chrome), so two people on the SAME code saw genuinely
// different-looking dropdowns depending on platform. `appearance: none` is
// the standard fix; the class backs it with one consistent chevron so a
// <select> reads as a plain styled input everywhere. See CLAUDE.md.
export const selectCls =
  `${inputCls} select-flat cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed`;
export const selectSmCls =
  `${inputSmCls} select-flat cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed`;

// Labels are semibold (owner review, Oct 27 2026): the term of a
// label/value pair reads as a definition, distinct from the value.
export const labelCls = "block text-meta font-semibold text-ink-muted mb-1";

// EYEBROW — the deliberate all-caps accent (owner, Oct 12 2026: "all caps …
// makes things look professional and differentiate"). Small, uppercase, wide
// tracking, muted. It labels a GROUP or a section — card titles, sidebar/filter
// groups, chart group labels, panel section titles — never a single value.
// ⚠️ Field labels (`labelCls`, `Field`) stay SENTENCE case: caps on every
// label is what made screens read as one undifferentiated wall.
export const eyebrowCls = "text-xs font-semibold text-ink-muted uppercase tracking-wider";

// Section/sidebar headings use the eyebrow. Form field labels use labelCls
// (different role — keep them separate).
export const sectionHeadingCls = eyebrowCls;

export const buttonPrimaryCls =
  "flex-1 bg-brand-600 hover:bg-brand-700 active:bg-brand-800 disabled:opacity-50 " +
  "text-white text-sm font-semibold py-2 rounded-md shadow-sm transition-colors " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2";

export const buttonSecondaryCls =
  "flex-1 bg-surface hover:bg-zinc-50 active:bg-zinc-100 disabled:opacity-50 text-ink text-sm font-medium " +
  "py-2 rounded-md border border-zinc-300 shadow-sm transition-colors " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2";

export const errorBannerCls =
  "text-danger-700 text-sm bg-danger-50 border border-danger-200 px-3 py-2 rounded-md";

export const iconDeleteCls = "text-ink-muted hover:text-danger-600 text-lg leading-none transition-colors";

export const cardCls = "bg-surface border border-line rounded-xl shadow-sm";

export const tableHeadRowCls = "sticky top-0 bg-surface-muted border-b border-line";
export const tableHeadCellCls = "text-left text-xs font-semibold text-ink-muted px-4 py-2.5 whitespace-nowrap";
export const tableRowHoverCls = "border-b border-line hover:bg-surface-inset transition-colors cursor-pointer";

// Active: soft brand tint — visually clear without shouting orange.
// Inactive: neutral inset, same pill shape.
export const filterPillActiveCls =
  "px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors " +
  "bg-brand-50 text-brand-700 border border-brand-300 hover:bg-brand-100";
export const filterPillInactiveCls =
  "px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors " +
  "bg-surface-inset text-ink-muted border border-line hover:bg-zinc-200 hover:text-ink";

export const modalOverlayCls = "fixed inset-0 z-panel bg-ink/50";
export const modalPanelCls = "bg-surface border border-line rounded-xl shadow-2xl";

export const buttonPrimarySmCls =
  "flex items-center gap-1.5 text-meta font-medium bg-brand-600 hover:bg-brand-700 " +
  "disabled:opacity-50 text-white px-3 py-1.5 rounded-md transition-colors " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2";

export const buttonSecondarySmCls =
  "flex items-center gap-1.5 text-meta font-medium bg-surface hover:bg-zinc-50 " +
  "disabled:opacity-50 text-ink border border-zinc-300 px-3 py-1.5 rounded-md " +
  "shadow-sm transition-colors " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2";

export const buttonNeutralSmCls =
  "flex items-center gap-1.5 text-meta font-medium bg-surface-inset hover:bg-zinc-200 " +
  "disabled:opacity-40 text-ink border border-zinc-300 px-3 py-1.5 rounded-md transition-colors " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2";

// Destructive actions (Delete Visit, Delete PO…). Same shape as the small
// buttons above so a Delete never looks like it came from another app; keep it
// apart from Edit/Save, not beside them (destructive-emphasis).
// Press feedback for icon-only buttons (from Fenario's job UI overhaul).
export const iconPressCls =
  "transition-[color,background-color,transform] duration-150 ease-out " +
  "active:duration-0 active:bg-zinc-200 active:scale-90 motion-reduce:active:scale-100";

export const buttonDangerSmCls =
  "flex items-center gap-1.5 text-meta font-medium bg-danger-50 hover:bg-danger-100 active:bg-danger-100 " +
  `disabled:opacity-50 text-danger-700 border border-danger-200 px-3 py-1.5 rounded-md transition-colors ` +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger-600 focus-visible:ring-offset-2";

// Capitalization (design audit M10, writing.md › "Choose a style for each UI
// element type"): Title Case for buttons, page and dialog titles, nav and tab
// labels ("Save and Close", "New User"); sentence case for field labels,
// hints, messages, and questions in a confirm title ("Remove this task?").
export const pageTitleCls = "text-2xl font-semibold text-ink";


// Standard page toolbar wrapper — keeps toolbar rows consistent across list pages.
export const toolbarCls = "flex flex-wrap items-center gap-3 mb-4";

// List-page toolbar rows. Row 1 is PageHeader (title + "+ New"); this is the
// controls row (search, filters, saved views, columns, export, pagination);
// toolbarCls closes the header with the status-pill row.
export const toolbarRowCls = "flex flex-wrap items-center gap-3 mb-3";

// The shared "short" search input width used by every list page.
export const searchInputWidthCls = "w-full sm:w-60";
